/**
 * Terrain — the whole-cell obstacle layer of the encounter. Plain-data, JSON-shaped
 * (the same no-class
 * invariant as the rest of the encounter), it lives **here in the rules/encounter
 * layer**, never in the pure `@lancer-vtt/space` package: the geometry core only
 * ever receives the *closures* this module builds (`blocked`/`moveCost`/
 * `blocksSight`), never the `TerrainMap` itself. That's the same discipline
 * `enterCost`/`occupancyKeys` already follow — the pure core stays rules-free.
 *
 * A cell stores a **tile reference** (`tiles.ts`'s catalogue, or a one-off custom
 * cell), and its mechanics are **typed properties, not a bool** (echoing COMP/CON's
 * per-tile bitmask), resolved through the one accessor {@link cellProperties}
 * (`decisions/0185`): a railing can block movement but not sight, smoke can grant
 * cover without blocking movement, difficult terrain costs extra move budget. Keyed by
 * the space's cell key (`"q,r"`), so it's grid-agnostic in shape — `TerrainMap`
 * and its derived closures are typed over the position `P` (here {@link Cell}),
 * not hard-coded to `Hex`, so a square grid slots in unchanged (the same
 * `Position` seam `move_token`'s `Cell` alias holds open).
 *
 * Edge walls + cover degrees live in a per-edge layer ({@link EdgeMap}) alongside the
 * footprint-folding {@link terrainVisibility}, *additively* — the whole-cell map and its own
 * closures work unchanged. The edge layer is keyed
 * by the **canonical shared-edge key** (`@lancer-vtt/space`'s `edgeKey`, the one
 * genuinely fiddly bit), and its movement/sight blockers fold into the SAME
 * `enterCost(to, from)` / `sight` seams the cell layer uses — no second system.
 *
 * **Grid-agnostic in body, not just shape.** Every cell→key and
 * edge→key derivation goes through the encounter's `Space<Cell>` (`space.key` /
 * `edgeKey(space, …)`), threaded in by the caller (the reducer/answerer pass
 * `worldFor(state)`; the hex-only display surfaces pass `hexSpace`) — there is no
 * hardcoded `hexSpace`/`hexKey` left, so a square encounter keys its terrain by
 * `"x,y"` and a hex encounter by `"q,r"` through the same functions.
 *
 * **`dangerous`/`hazard` (Core p.62)** are a THIRD, orthogonal
 * flag pair alongside movement/sight — a cell's danger never changes `moveCost`/
 * `blocksMovement` (dangerous ≠ difficult; a cell can be either, neither, or both).
 * The data lives here (`cellDangerous`/`cellHazard`), but the trigger — the
 * ENGINEERING check + 5 typed damage on first entry or end-of-turn — is rules
 * (dice), so it lives in `@lancer-vtt/rules`' `resolveDangerousTerrainCheck`, not here.
 */

import {
  edgeKey,
  sight,
  visibility,
  type SightResult,
  type Space,
} from "@lancer-vtt/space";
import type { AreaEffect, AreaEffectId, Cell, Combatant, CombatantId, DamageType } from "./types";
import { objectContributions, type ObjectContributions } from "./objectContributions";
import { areaContributions, type AreaContributions } from "./areaContributions";
import { CUSTOM_TILE, OPEN_GROUND_TILE, terrainCellProblem, tileProperties, type TileTypeId } from "./tiles";

/**
 * A cell's mechanical terrain properties. Every field is optional (an absent
 * flag means "ordinary open ground"). A cell never stores these directly: it
 * stores a {@link TerrainCell} (a tile reference), and the engine resolves the
 * properties through {@link cellProperties} (`decisions/0185`).
 */
export type TerrainProperties = {
  /** Solid to movement — a footprint may not cover this cell (`enterCost` ⇒ ∞). */
  blocksMovement?: boolean;
  /**
   * **Full-height** — solid even to a Flying mover. RAW p.63 makes ignore-obstructions the
   * FLIGHT default and full-height
   * the exception ("couldn't move through a gap smaller than their mech"); this
   * flag is the 2D-faithful instance of that exception (an indoor floor-to-ceiling
   * wall). **Only meaningful — and only ever stored — alongside `blocksMovement:
   * true`**: a flight-only barrier with no ground effect has no 2D physical
   * reading, so the sparsifier (`normalizeFlags` below) enforces the coupling by
   * construction rather than trusting callers. Absent ⇒ flyable (default-flyable).
   */
  blocksFlight?: boolean;
  /** Difficult terrain: extra cost to enter (default 1 when absent). Must be `>= 1`. */
  moveCost?: number;
  /** Blocks a sightline passing through this cell. */
  blocksSight?: boolean;
  /** Cover this cell grants a sightline passing through it. */
  cover?: "none" | "soft" | "hard";
  /**
   * **Dangerous terrain** (Core p.62) — deals damage on first
   * entering the cell in a round or ending a turn in it, unless an ENGINEERING check
   * (vs 10) succeeds. Orthogonal to `moveCost`/`blocksMovement`: a cell can be
   * dangerous AND difficult (lava you can still trudge through), or dangerous and
   * perfectly easy to cross (an exposed minefield) — this flag never changes the
   * movement cost/legality the reducer charges — only the hazard trigger
   * (`resolveDangerousTerrainCheck` in `@lancer-vtt/rules`) reads it.
   */
  dangerous?: boolean;
  /**
   * The hazard's damage type, read only when `dangerous` is true. Stored (even at
   * the kinetic default) so a later lava-vs-acid distinction is a paint change, not
   * a migration. Sparsified away when it equals the default `"kinetic"`.
   */
  hazard?: DamageType;
}

/**
 * One stored terrain cell: a catalogue tile reference, or a one-off `"custom"` cell carrying
 * its own properties (`decisions/0185`). Only UI naming code reads `.tile`; every rule reads
 * the cell's mechanics through {@link cellProperties} or the predicates below.
 */
export type TerrainCell =
  | { tile: TileTypeId }
  | { tile: typeof CUSTOM_TILE; properties: TerrainProperties };

/**
 * The encounter's terrain: a sparse map of cell key (`"q,r"`) → {@link TerrainCell}.
 * Plain JSON (a `Record`, not a `Map` — the no-class invariant). Absent key ⇒
 * ordinary open ground. This is the *data*; the closures below are what the pure
 * geometry core consumes.
 */
export type TerrainMap = Record<string, TerrainCell>;

/** An empty terrain map — the default for a fresh encounter (featureless field). */
export function emptyTerrain(): TerrainMap {
  return {};
}

/**
 * **The one accessor** (`decisions/0185`): a cell's mechanical properties, resolved through its
 * tile, or `undefined` for open ground. Every predicate below reads through it.
 */
export function cellProperties(space: Space<Cell>, terrain: TerrainMap, cell: Cell): TerrainProperties | undefined {
  return propertiesAtKey(terrain, space.key(cell));
}

/** {@link cellProperties} for a caller already holding the cell key. */
export function propertiesAtKey(terrain: TerrainMap, key: string): TerrainProperties | undefined {
  const entry = terrain[key];
  return entry === undefined ? undefined : tileProperties(entry);
}

/**
 * Every stored cell's key and resolved properties — for the whole-map passes (solid-key sets,
 * renderers, legends) that used to iterate the flag map directly.
 */
export function terrainPropertyEntries(terrain: TerrainMap): [string, TerrainProperties][] {
  return Object.entries(terrain).map(([key, entry]) => [key, tileProperties(entry)]);
}

/** The stored cell at `cell` (tile reference), or `undefined` for open ground. UI naming only — rules read {@link cellProperties}. */
export function terrainCellAt(space: Space<Cell>, terrain: TerrainMap, cell: Cell): TerrainCell | undefined {
  return terrain[space.key(cell)];
}

/** Whether terrain makes `cell` solid to movement (a footprint may not cover it). */
export function blocksMovement(space: Space<Cell>, terrain: TerrainMap, cell: Cell): boolean {
  return cellProperties(space, terrain, cell)?.blocksMovement === true;
}

/** Whether terrain blocks a sightline passing through `cell`. */
export function blocksSight(space: Space<Cell>, terrain: TerrainMap, cell: Cell): boolean {
  return cellProperties(space, terrain, cell)?.blocksSight === true;
}

/**
 * Whether `cell` is solid even to a **Flying** mover (full-height). Only ever `true`
 * alongside {@link blocksMovement}
 * (the sparsifier enforces the coupling — see `normalizeFlags`), so callers that
 * already gate on `blocksMovement` can treat this as the narrower "and it's also
 * flight-solid" refinement.
 */
export function blocksFlight(space: Space<Cell>, terrain: TerrainMap, cell: Cell): boolean {
  return cellProperties(space, terrain, cell)?.blocksFlight === true;
}

/**
 * Cost to enter `cell` given terrain — `moveCost` when set (difficult terrain),
 * else 1 (ordinary ground). Always `>= 1`. Solid cells are gated by
 * {@link blocksMovement}/the occupancy set *before* this is consulted, so this
 * only scales the budget cost of an otherwise-legal cell.
 */
export function moveCost(space: Space<Cell>, terrain: TerrainMap, cell: Cell): number {
  const c = cellProperties(space, terrain, cell)?.moveCost;
  return typeof c === "number" && c >= 1 ? c : 1;
}

/** The default hazard damage type for a dangerous cell that doesn't override `hazard` (Core p.62 leaves the type to the GM; kinetic is the sane default — e.g. rubble/exposed terrain). */
export const DEFAULT_HAZARD_TYPE: DamageType = "kinetic";

/** Whether `cell` is **dangerous terrain** — the hazard trigger reads this, never movement. */
export function cellDangerous(space: Space<Cell>, terrain: TerrainMap, cell: Cell): boolean {
  return cellProperties(space, terrain, cell)?.dangerous === true;
}

/** The damage type a dangerous cell deals (kinetic default) — meaningless when {@link cellDangerous} is false. */
export function cellHazard(space: Space<Cell>, terrain: TerrainMap, cell: Cell): DamageType {
  return cellProperties(space, terrain, cell)?.hazard ?? DEFAULT_HAZARD_TYPE;
}

/**
 * The set of cell keys terrain makes **solid** — the terrain half of the one
 * shared `blocked` occupancy predicate. The
 * reducer unions this with the token-occupancy keys so movement and collision
 * flow through a single set; there is no separate terrain-blocking pass.
 */
export function solidTerrainKeys(terrain: TerrainMap): Set<string> {
  const keys = new Set<string>();
  for (const [key, flags] of terrainPropertyEntries(terrain)) {
    if (flags.blocksMovement === true) keys.add(key);
  }
  return keys;
}

/**
 * The subset of {@link solidTerrainKeys} that's solid even to a **Flying** mover
 * (full-height cells) — `movementContext`'s
 * seam for routing a Flying mover's full-height cells to `blocked` instead of the
 * `noStop` every other solid cell gets. Always a subset of `solidTerrainKeys`'
 * output (the sparsifier coupling guarantees `blocksFlight` never survives without
 * `blocksMovement`), but this filters independently rather than assuming it, so a
 * future relaxation of the coupling can't silently widen this set too.
 */
export function flightBlockingTerrainKeys(terrain: TerrainMap): Set<string> {
  const keys = new Set<string>();
  for (const [key, flags] of terrainPropertyEntries(terrain)) {
    if (flags.blocksFlight === true) keys.add(key);
  }
  return keys;
}

/**
 * Center-to-center line-of-sight between two cells through this terrain — the
 * single-line primitive (whole-cell blocking + edge walls). The encounter-layer binding of the
 * pure {@link sight}: it builds the
 * `blocksCell` closure from cell `blocksSight` and (when an {@link EdgeMap} is
 * passed) the `blocksEdge` closure from edge `blocksSight`, handing both to the
 * grid-agnostic core. Returns a {@link SightResult}.
 *
 * `edges` is optional — omit it for cell-only LoS.
 */
/** Named arguments for {@link terrainSight}. */
export type TerrainSightArgs = {
  space: Space<Cell>;
  terrain: TerrainMap;
  a: Cell;
  b: Cell;
  edges?: EdgeMap;
}

export function terrainSight(args: TerrainSightArgs): SightResult {
  const { space, terrain, a, b, edges } = args;
  return sight({
    space,
    a,
    b,
    blocksCell: (c) => blocksSight(space, terrain, c),
    blocksEdge: edges ? (from, to) => edgeBlocksSight({ space, edges, a: from, b: to }) : undefined,
  });
}

// ── Edge layer ────────────────────────────────────────────────────────────────────

/**
 * Per-edge terrain flags — a thin wall on the boundary *between* two adjacent
 * cells (two units stand on either side of it; a whole-cell blocker can't express
 * that). Mirrors {@link TerrainProperties}'s movement/sight flags where they make sense
 * for a barrier: no `moveCost` (an edge is crossed or not — there's no "difficult
 * edge"), no `cover` yet (cover degrees fold over cells; a wall fully blocks).
 * Every field optional — an absent entry is "open boundary".
 */
export type EdgeTerrain = {
  /** Solid to movement — you can't step across this edge (`enterCost` ⇒ ∞). */
  blocksMovement?: boolean;
  /**
   * **Full-height wall** — blocks a Flying mover's crossing too (mirrors
   * {@link TerrainProperties.blocksFlight}). Only
   * meaningful — and only ever stored — alongside `blocksMovement: true`; the
   * sparsifier (`normalizeEdgeFlags` below) enforces the coupling. Absent ⇒
   * flyable (a low wall a flyer clears).
   */
  blocksFlight?: boolean;
  /** Blocks a sightline crossing this edge (edge-aware LoS). */
  blocksSight?: boolean;
  /**
   * Cover this edge grants a sightline crossing it.
   * **Deliberately NOT coupled to `blocksMovement`** — the direct opposite
   * of {@link blocksFlight}'s coupling. RAW p.66 names the physical reading by name:
   * soft cover includes a line "broken up by smoke, trees, or **fences**" — a fence
   * you can shoot over *and* step over is cover without blocking movement at all.
   * Unlike a flight-only barrier (no coherent 2D physical reading), edge cover has a
   * perfectly good independent one, so the sparsifier (`normalizeEdgeFlags` below)
   * keeps `cover` whenever it's `"soft"`/`"hard"`, regardless of `blocksMovement`.
   * Absent/`"none"` ⇒ no cover (default).
   */
  cover?: "none" | "soft" | "hard";
}

/**
 * The encounter's edge terrain: a sparse map of **canonical shared-edge key** →
 * {@link EdgeTerrain}. The key is `@lancer-vtt/space`'s `edgeKey(space, a, b)` —
 * order-independent, so a wall on the A↔B edge is found whether you approach from
 * A or B (the one genuinely fiddly bit, canonicalized once in the space package
 * and tested hard there). Plain JSON (a `Record`). Absent key ⇒ open boundary.
 */
export type EdgeMap = Record<string, EdgeTerrain>;

/** An empty edge map — the default for a fresh encounter (no walls). */
export function emptyEdges(): EdgeMap {
  return {};
}

/** The canonical key for the edge between adjacent cells `a` and `b`, via the encounter's space. */
export function edgeMapKey(space: Space<Cell>, a: Cell, b: Cell): string {
  return edgeKey(space, a, b);
}

/** Named arguments for the edge lookups ({@link edgeTerrain}/{@link edgeBlocksMovement}/{@link edgeBlocksSight}). */
export type EdgeLookupArgs = {
  space: Space<Cell>;
  edges: EdgeMap;
  a: Cell;
  b: Cell;
}

/** Look up an edge's flags (or `undefined` for an open boundary). */
export function edgeTerrain(args: EdgeLookupArgs): EdgeTerrain | undefined {
  const { space, edges, a, b } = args;
  return edges[edgeMapKey(space, a, b)];
}

/** Whether an edge wall blocks *movement* across the `a`↔`b` boundary. */
export function edgeBlocksMovement(args: EdgeLookupArgs): boolean {
  const { space, edges, a, b } = args;
  return edges[edgeMapKey(space, a, b)]?.blocksMovement === true;
}

/** Whether an edge wall blocks a *sightline* crossing the `a`↔`b` boundary. */
export function edgeBlocksSight(args: EdgeLookupArgs): boolean {
  const { space, edges, a, b } = args;
  return edges[edgeMapKey(space, a, b)]?.blocksSight === true;
}

/**
 * Whether an edge wall blocks a **Flying** mover crossing the `a`↔`b` boundary
 * (full-height wall — mirrors {@link blocksFlight} for the edge layer). Only ever `true` alongside
 * {@link edgeBlocksMovement} (the sparsifier coupling, `normalizeEdgeFlags`).
 */
export function edgeBlocksFlight(args: EdgeLookupArgs): boolean {
  const { space, edges, a, b } = args;
  return edges[edgeMapKey(space, a, b)]?.blocksFlight === true;
}

/**
 * Cover degree an edge wall grants a sightline crossing it (mirrors {@link cellCover} for the
 * edge layer). Reads
 * `"none"` for an open boundary or a wall with no `cover` set — **including a
 * solid `blocksMovement` wall**, since `cover` is orthogonal, not inherited from
 * `blocksMovement` the way {@link edgeBlocksFlight} inherits its coupling.
 */
export function edgeCover(args: EdgeLookupArgs): "none" | "soft" | "hard" {
  const { space, edges, a, b } = args;
  return edges[edgeMapKey(space, a, b)]?.cover ?? "none";
}

/** Normalize an `EdgeTerrain`: drop default/`undefined` flags so the map stays sparse and equal flag sets compare equal. */
function normalizeEdgeFlags(flags: EdgeTerrain): EdgeTerrain {
  const out: EdgeTerrain = {};
  if (flags.blocksMovement === true) out.blocksMovement = true;
  // Coupled to `blocksMovement` — a flight-only edge has no 2D physical
  // reading, so it's dropped unless the edge is already solid to ground movers.
  if (out.blocksMovement === true && flags.blocksFlight === true) out.blocksFlight = true;
  if (flags.blocksSight === true) out.blocksSight = true;
  // NOT coupled to `blocksMovement` (the deliberate contrast with `blocksFlight`
  // above) — a fence grants cover whether or not it's solid to movement, so this
  // reads `flags.cover` directly rather than gating on `out.blocksMovement`.
  if (flags.cover === "soft" || flags.cover === "hard") out.cover = flags.cover;
  return out;
}

/**
 * **Replace** an edge's flags wholesale, returning a **new** {@link EdgeMap}
 * (the reducer never mutates in place). Keyed by the canonical edge, so setting
 * `(a,b)` and `(b,a)` touch the same entry. Replace-semantics (symmetric inverse:
 * capture prior, set new, inverse sets prior back); normalized + sparsified, so an
 * edge left with no meaningful flags (`{}`) is removed and the map round-trips to
 * the open/default case.
 */
/** Named arguments for {@link setEdgeTerrain}. */
export type SetEdgeTerrainArgs = {
  space: Space<Cell>;
  edges: EdgeMap;
  a: Cell;
  b: Cell;
  flags: EdgeTerrain;
}

export function setEdgeTerrain(args: SetEdgeTerrainArgs): EdgeMap {
  const { space, edges, a, b, flags } = args;
  const key = edgeMapKey(space, a, b);
  const norm = normalizeEdgeFlags(flags);
  const next = { ...edges };
  if (Object.keys(norm).length === 0) delete next[key];
  else next[key] = norm;
  return next;
}

// ── Footprint-folding visibility + cover ─────────────────────────────────────────

/**
 * Cover degree a cell grants when a sightline passes through it — read from the
 * cell's `cover` flag (`"soft"`/`"hard"`). The cell-layer half of the cover fold;
 * `terrainVisibility` hands these to the pure {@link visibility}.
 */
export function cellCover(space: Space<Cell>, terrain: TerrainMap, cell: Cell): "none" | "soft" | "hard" {
  return cellProperties(space, terrain, cell)?.cover ?? "none";
}

/**
 * **Footprint-folding** line-of-sight + cover between two units' *spaces* — the RAW "any part of
 * your space to any part of theirs" query. `from`/`to` are footprints (each a `Cell[]`, e.g.
 * `space.footprint(anchor, size).cells()`; a Size-1 unit is one cell). Binds the
 * pure {@link visibility}, deriving every closure from terrain flags:
 *
 *  - `blocksCell`/`blocksEdge` — full sight-blocking (cell `blocksSight`, edge
 *    `blocksSight`), the same predicates {@link terrainSight} uses, so the
 *    single-line and multi-line queries agree on what *blocks*.
 *  - `softCell`/`hardCell` — cover-granting cells (`cover: "soft"`/`"hard"`).
 *  - `softEdge`/`hardEdge` — cover-granting edges (`EdgeTerrain.cover`,
 *    read via {@link edgeCover}), e.g. a low wall you can shoot over but not through.
 *
 * `visibility` folds across the footprint cross-product to a real cover degree
 * (`none`/`soft`/`hard`) per the rulebook (p. 65): a fully-clear line ⇒ no cover
 * (and defeats hard cover by flanking); hard cover only when the target footprint
 * is adjacent to it. See {@link visibility} for the exact fold + documented
 * simplifications (center sampling; SIZE clause deferred).
 */
/** Named arguments for {@link terrainVisibility}. */
export type TerrainVisibilityArgs = {
  space: Space<Cell>;
  terrain: TerrainMap;
  from: Cell[];
  to: Cell[];
  edges?: EdgeMap;
  /**
   * Placed combatants — when
   * supplied, every placed, non-Destroyed `object`/`deployable`'s footprint ×
   * `objectProfile` folds into the SAME `blocksCell`/`softCell`/`hardCell` closures
   * terrain feeds (`objectContributions.ts`), so a wall/boulder affects LoS/cover
   * exactly like a terrain cell would, and stops the instant it's destroyed (no
   * caching — the next call already reflects it). **Optional** so a caller with no
   * combatants in scope (a pure terrain-only query) is unaffected.
   */
  combatants?: Record<CombatantId, Combatant>;
  /**
   * The combatant `to` is attacking-against, when it IS itself a cover-contributing
   * object/deployable. Excludes
   * ONLY that combatant's own cells from `objectContributions`' soft/hard-cover
   * sets — never terrain (terrain is never the target) and never another,
   * overlapping object's cover on the same cell. Optional and default-undefined so
   * a call site not attacking a specific, known combatant behaves unchanged; pass it
   * whenever the caller knows the attack's target. See `objectContributions`'s own doc
   * for the mechanism.
   */
  excludeCoverFrom?: CombatantId;
  /**
   * Live standing area effects — when
   * supplied, every entry's disk folds into the SAME `softCell` closure terrain +
   * object contributions feed (`areaContributions.ts`), so a smoke cloud grants
   * cover exactly like a terrain/object cover cell would, live: walking in grants it,
   * walking out drops it, with no area-specific code anywhere in this function.
   * **Never excludable via `excludeCoverFrom`** — that parameter scopes to a single
   * cover-contributing *combatant*, and an area has no combatant identity to exclude, so it is
   * never suppressed by it (pinned in `terrain.test.ts`).
   * Optional so a caller with no areas in scope (a pure terrain/object query) is unaffected.
   */
  areaEffects?: Record<AreaEffectId, AreaEffect>;
  /**
   * Extra hard-cover cell keys (`space.key`) the caller derived from content this package cannot
   * read — the Goliath's Guardian, a character that is hard cover for its allies only
   * (`alliedCoverCells`, `@lancer-vtt/rules`). Folded into the SAME `hardCell` closure as terrain
   * and objects, so the line and adjacency geometry is identical. Optional; omitted means none.
   */
  extraHardCoverCells?: ReadonlySet<string>;
}

export function terrainVisibility(args: TerrainVisibilityArgs): SightResult {
  const { space, terrain, from, to, edges, combatants, excludeCoverFrom, areaEffects, extraHardCoverCells } = args;
  return foldTerrainVisibility({
    space,
    terrain,
    from,
    to,
    edges,
    contrib: combatants ? objectContributions(space, combatants, excludeCoverFrom) : undefined,
    areaContrib: areaEffects ? areaContributions(space, areaEffects, from) : undefined,
    extraHardCoverCells,
  });
}

/**
 * {@link terrainVisibility} over contributions the caller already built — the seam that lets a
 * per-cell sweep (`sightlinesFrom`, `sightlines.ts`) build the object and area sets once per pass
 * instead of once per cell. `contrib` must already carry the target's `excludeCoverFrom`, and
 * `areaContrib` must have been built with `from` as its viewer.
 */
export function foldTerrainVisibility(args: {
  space: Space<Cell>;
  terrain: TerrainMap;
  from: Cell[];
  to: Cell[];
  edges: EdgeMap | undefined;
  contrib: ObjectContributions | undefined;
  areaContrib: AreaContributions | undefined;
  extraHardCoverCells: ReadonlySet<string> | undefined;
}): SightResult {
  const { space, terrain, from, to, edges, contrib, areaContrib, extraHardCoverCells } = args;
  /** Terrain/object soft cover — identical on both the line and occupancy paths. */
  const staticSoftCell = (c: Cell) =>
    cellCover(space, terrain, c) === "soft" || !!contrib?.softCoverCells.has(space.key(c));
  /**
   * The **line** (intervening) soft predicate handed to `visibility()`. Area cover enters
   * via `interveningSoftCoverCells`, which withholds any cloud the ATTACKER is standing in:
   * a cloud you are inside is not between you and your target, so it
   * never confers cover on the units you shoot at from within it.
   */
  const lineSoftCell = (c: Cell) =>
    staticSoftCell(c) || !!areaContrib?.interveningSoftCoverCells.has(space.key(c));
  /**
   * The **occupancy** soft predicate (the floor below). Reads the FULL area set — an area
   * covers whoever stands in it unconditionally (Core p.120: "All characters and objects
   * within the blast area benefit from soft cover"), including when the attacker happens to
   * be standing in that same cloud.
   */
  const occupancySoftCell = (c: Cell) =>
    staticSoftCell(c) || !!areaContrib?.softCoverCells.has(space.key(c));
  const hardCell = (c: Cell) =>
    cellCover(space, terrain, c) === "hard" || !!contrib?.hardCoverCells.has(space.key(c)) || !!extraHardCoverCells?.has(space.key(c));
  // Edge cover — a low wall's `cover` flag, folded via the SAME `softEdge`/`hardEdge`
  // closures `visibility()` accepts. No object/area equivalent exists for edges (only cells and
  // combatants occupy space), so unlike the cell closures above these read straight
  // off `edgeCover`, no union with a contributions set.
  const softEdge = edges ? (a: Cell, b: Cell) => edgeCover({ space, edges, a, b }) === "soft" : undefined;
  const hardEdge = edges ? (a: Cell, b: Cell) => edgeCover({ space, edges, a, b }) === "hard" : undefined;
  const view = visibility({
    space,
    from,
    to,
    blocksCell: (c) => blocksSight(space, terrain, c) || !!contrib?.blocksSightCells.has(space.key(c)),
    blocksEdge: edges ? (a, b) => edgeBlocksSight({ space, edges, a, b }) : undefined,
    softCell: lineSoftCell,
    hardCell,
    softEdge,
    hardEdge,
  });

  // **Occupancy floor**: "any time a target is obscured or
  // obstructed somehow, it has soft cover" (Core p.66) is an occupancy grant, not
  // just a line-intersection one — a target standing ON a cover cell gets soft
  // cover even with zero intermediate cells between it and the attacker (the near
  // rim of a cloud, or any adjacent shot into foliage/smoke/etc). Reuses the SAME
  // hardCell union `visibility()` was just given, plus `occupancySoftCell` — which
  // differs from the line predicate ONLY in that it keeps the attacker's own cloud
  // (the occupancy grant is unconditional), so a target standing in the SAME cloud as its attacker is
  // still covered by it. Deliberately outside `visibility()`: `anyClear` there
  // short-circuits a clear line to `cover: "none"` before a floor inside the spine
  // walk could ever bite (the spine walk also skips both endpoints). Target-side
  // only — reads `to`, never `from`. A floor, never an override: a target
  // already in `hard` cover on the line stays `hard`, and `view.visible` is never
  // touched (the area-LoS exclusion reads that bit). Hard-cover
  // occupancy folds to `soft`, never `hard` — hard cover keeps its own printed
  // adjacency (+Size) clause, gated by `targetAdjacentToHard` above, which this
  // floor does not touch or widen.
  //
  // **No edge equivalent, deliberately**: a target can never "occupy"
  // an edge the way it occupies a cell, so there is no `occupancyHardEdge`/
  // `occupancySoftEdge` to union in here. More importantly, one isn't needed: the
  // occupancy floor exists only because `visibility()`'s spine walk skips BOTH
  // endpoint *cells* (a unit standing on its own cover cell gets no credit from the
  // line walk itself). The spine walk does NOT skip endpoint-touching *edges* —
  // `sight`/`visibility`'s edge loop is explicitly endpoint-inclusive (see
  // `softEdge`/`hardEdge`'s docs in `los.ts`) — so when attacker and target are
  // adjacent (a 2-cell spine) the single edge between them is already walked and
  // already counted. Adding a floor here would double-count that same edge.
  const occupiesCover = to.some((c) => occupancySoftCell(c) || hardCell(c));
  return view.visible && view.cover === "none" && occupiesCover
    ? { visible: true, cover: "soft" }
    : view;
}

/**
 * Normalize a {@link TerrainProperties} bag: drop `undefined`/default-valued flags so equal
 * property sets compare equal and a custom cell stays sparse. Every catalogue tile's properties
 * are a fixed point of this (pinned in `tiles.test.ts`), so no tile carries dead data.
 */
export function normalizeTerrainProperties(flags: TerrainProperties): TerrainProperties {
  const out: TerrainProperties = {};
  if (flags.blocksMovement === true) out.blocksMovement = true;
  // Coupled to `blocksMovement` — a flight-only cell has no 2D physical
  // reading, so it's dropped unless the cell is already solid to ground movers.
  if (out.blocksMovement === true && flags.blocksFlight === true) out.blocksFlight = true;
  if (typeof flags.moveCost === "number" && flags.moveCost > 1) out.moveCost = flags.moveCost;
  if (flags.blocksSight === true) out.blocksSight = true;
  if (flags.cover === "soft" || flags.cover === "hard") out.cover = flags.cover;
  if (flags.dangerous === true) {
    out.dangerous = true;
    // Only store `hazard` when it diverges from the default — keeps a plain
    // kinetic-hazard cell as sparse as a solid/cover one.
    if (flags.hazard !== undefined && flags.hazard !== DEFAULT_HAZARD_TYPE) out.hazard = flags.hazard;
  }
  return out;
}

/**
 * A property bag as a string that is equal exactly when two bags mean the same mechanics: the
 * {@link normalizeTerrainProperties} form with its keys sorted, as JSON. The terrain art's custom
 * regions and the terrain mechanics regions both group by it, and the art seeds its wobble from
 * it, so its output must never change for an existing bag.
 */
export function terrainPropertiesKey(properties: TerrainProperties): string {
  const p = normalizeTerrainProperties(properties);
  return JSON.stringify(Object.fromEntries(Object.entries(p).sort(([a], [b]) => (a < b ? -1 : 1))));
}

/**
 * The stored form of a painted cell, or `undefined` when painting it clears the cell: open
 * ground, and a custom cell whose properties normalize to nothing. A custom cell's properties
 * are normalized; a catalogue reference is stored as the bare id. The caller has already checked
 * the id ({@link terrainCellProblem}).
 */
export function normalizeTerrainCell(entry: TerrainCell): TerrainCell | undefined {
  if (entry.tile === CUSTOM_TILE) {
    const properties = normalizeTerrainProperties(entry.properties);
    return Object.keys(properties).length === 0 ? undefined : { tile: CUSTOM_TILE, properties };
  }
  return entry.tile === OPEN_GROUND_TILE ? undefined : { tile: entry.tile };
}

/** A custom cell carrying `properties` — what the mechanic brushes and CLI flags paint. */
export function customTerrainCell(properties: TerrainProperties): TerrainCell {
  return { tile: CUSTOM_TILE, properties };
}

/**
 * Why `raw` is not a valid stored {@link TerrainMap}, naming the first bad cell, or `null` when
 * it is valid. The load walls (encounter and scene) and `apply_terrain_map` all check through
 * this, so a flag-format or unknown-id cell never reaches state.
 */
export function terrainMapProblem(raw: unknown): string | null {
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) return "terrain is not a cell map";
  for (const [key, cell] of Object.entries(raw)) {
    const problem = terrainCellProblem(cell);
    if (problem !== null) return `terrain cell ${key} ${problem}`;
  }
  return null;
}

/**
 * **Replace** a cell's terrain wholesale, returning a **new** `TerrainMap`
 * (the reducer never mutates in place). Replace-semantics, not merge, so the
 * inverse is symmetric: capture the prior cell, set the new one, and the
 * inverse sets the prior back. The cell is normalized ({@link normalizeTerrainCell}),
 * and painting open ground or an empty custom cell removes the key — so the map
 * stays sparse and clearing round-trips to the empty/default case.
 */
/** Named arguments for {@link setCellTerrain}. */
export type SetCellTerrainArgs = {
  space: Space<Cell>;
  terrain: TerrainMap;
  cell: Cell;
  entry: TerrainCell;
}

export function setCellTerrain(args: SetCellTerrainArgs): TerrainMap {
  const { space, terrain, cell, entry } = args;
  const key = space.key(cell);
  const norm = normalizeTerrainCell(entry);
  const next = { ...terrain };
  if (norm === undefined) delete next[key];
  else next[key] = norm;
  return next;
}
